import {Controller} from "@hotwired/stimulus"

// Drag a table's rows into an order and save it.
//
// SortableJS with the task manager's options — `Sortable` is global, from the asset
// pipeline. Not jQuery UI's sortable.
//
// Same conventions as the boards: .sortable-item rows, a .sortable-handle to grab.
// The element is the tbody, each row carries its id, and the new order is posted as
// ids[] in the order they now sit, so the server does not have to work out what moved.
export default class extends Controller {
    static values = {url: String}

    connect() {
        if (typeof Sortable === "undefined") return

        this.sortable = new Sortable(this.element, {
            animation: 150,
            handle: ".sortable-handle",
            draggable: ".sortable-item",
            fallbackOnBody: true,
            direction: "vertical",
            swapThreshold: 0.65,
            // The row is lifted out of the table's layout while it is dragged, and a
            // <tr> that has left it collapses to its content — so the cell widths are
            // pinned on the way out and released on the way back in.
            onStart: (event) => this.pinWidths(event.item),
            onEnd: (event) => {
                this.releaseWidths(event.item)
                if (event.oldIndex !== event.newIndex) this.save()
            }
        })
    }

    disconnect() {
        if (this.sortable) this.sortable.destroy()
        this.sortable = null
    }

    pinWidths(row) {
        Array.from(row.children).forEach((cell) => {
            cell.dataset.pinnedWidth = cell.style.width
            cell.style.width = `${cell.getBoundingClientRect().width}px`
        })
    }

    releaseWidths(row) {
        Array.from(row.children).forEach((cell) => {
            cell.style.width = cell.dataset.pinnedWidth || ""
            delete cell.dataset.pinnedWidth
        })
    }

    save() {
        const body = new FormData()
        this.rows.forEach((row) => body.append("ids[]", row.dataset.sortableRowId))

        fetch(this.urlValue, {
            method: "PATCH",
            credentials: "same-origin",
            headers: {"X-CSRF-Token": this.csrfToken, Accept: "application/json"},
            body: body
        }).then((response) => {
            // Reload rather than leave an order on screen that is not in the database.
            if (!response.ok) window.location.reload()
        }).catch(() => window.location.reload())
    }

    get rows() {
        return Array.from(this.element.querySelectorAll("[data-sortable-row-id]"))
    }

    get csrfToken() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }
}
